<template>
  <div
    :class="[
      $style.promotionTypeCard,
      { [$style.boxSelected]: isSelected },
      { [$style.boxDisabled]: disabled },
      { [$style.hasAccent]: showAccent },
      { [$style.fillColumnHeight]: fillColumnHeight },
    ]"
    :style="rootStyle"
    @click="onCardClick"
  >
    <div :class="$style.header" @click.stop>
      <HgRadioButton
        :name="name"
        :modelValue="String(modelValue)"
        :inputValue="inputValue"
        :disabled="disabled"
        :label="header"
        @update:modelValue="onRadioUpdate"
      />
    </div>
    <p v-if="introTrimmed.length" :class="$style.intro">{{ introTrimmed }}</p>
    <div :class="[$style.benefits, { [$style.benefitsImproved]: improvedBenefits }]">
      <div v-for="(line, index) in benefitLines" :key="index" :class="$style.benefitItem">
        <HgIcon icon="Checkmark" :class="$style.checkIcon" size="large" />
        <span :class="$style.benefitLineWrap">
          <span :class="$style.benefitLineText">{{ line }}</span>
          <span
            v-if="showBenefitLine2Footnote && index === 1"
            :class="$style.benefitLine2TooltipWrap"
            @click.stop
          >
            <HgTooltip mode="hybrid" placement="top" theme="secondary">
              <p :class="$style.benefitLine2Footnote">{{ benefitLine2Footnote }}</p>
            </HgTooltip>
          </span>
        </span>
      </div>
    </div>
    <div v-if="displayPricingSlot" :class="$style.pricing">
      <slot></slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { HgIcon, HgRadioButton, HgTooltip } from '@smg-real-estate/domus';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    name: string;
    inputValue: string;
    modelValue: string;
    header: string;
    benefitLines: string[];
    disabled?: boolean;
    displayPricingSlot: boolean;
    /** Border and 8px top accent; hover/selected derive via `color-mix`. */
    accentBorderColor?: string;
    /** When selected, overrides default selected background (e.g. per-column tint). */
    selectedBackgroundColor?: string;
    /** Stretch to fill a flex column (equal-height card rows). */
    fillColumnHeight?: boolean;
    /** Short lead-in above benefit bullets (e.g. feature-flag copy). */
    intro?: string;
    /** Extra styling hook for improved benefit lines (e.g. typography). */
    improvedBenefits?: boolean;
    /** When true, second benefit line shows an inline info tooltip. */
    showBenefitLine2Footnote?: boolean;
    /** Tooltip body for the line-2 footnote; ignored when `showBenefitLine2Footnote` is false. */
    benefitLine2Footnote?: string;
  }>(),
  {
    disabled: false,
    accentBorderColor: undefined,
    selectedBackgroundColor: undefined,
    fillColumnHeight: false,
    intro: '',
    improvedBenefits: false,
    showBenefitLine2Footnote: false,
    benefitLine2Footnote: '',
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const isSelected = computed(() => props.inputValue === props.modelValue);

const introTrimmed = computed(() => props.intro.trim());

const showAccent = computed(() => !!props.accentBorderColor?.length && !props.disabled);

const accentStyle = computed(() => {
  if (!showAccent.value || !props.accentBorderColor) {
    return undefined;
  }
  const c = props.accentBorderColor;
  return {
    '--ptc-border-accent': c,
    '--ptc-border-accent-hover': `color-mix(in srgb, ${c} 82%, black)`,
  } as Record<string, string>;
});

const rootStyle = computed((): Record<string, string> | undefined => {
  const style: Record<string, string> = { ...(accentStyle.value ?? {}) };
  if (isSelected.value && props.selectedBackgroundColor) {
    style.backgroundColor = props.selectedBackgroundColor;
  }
  return Object.keys(style).length ? style : undefined;
});

function onRadioUpdate(value: string): void {
  if (props.disabled) {
    return;
  }
  emit('update:modelValue', value);
}

function onCardClick(): void {
  if (props.disabled) {
    return;
  }
  emit('update:modelValue', props.inputValue);
}
</script>

<style lang="scss" module>
@import '@/assets/variables';
@import '@/assets/mixins';

$toplisting-color: $color-dove-90;
$toplisting-hover-color: $color-dove-100;
$toplisting-selected-color: $color-dove-70;
$toplisting-selected-background: $color-dove-0;

.pricing {
  padding: $space-100;
  margin-top: auto;
  border-top: 1px solid $toplisting-color;

  @include body-3-bold;
}

.header,
.benefits {
  padding: $space-75 $space-100;
}

.intro {
  flex-shrink: 0;
  padding: $space-75 $space-100 0 $space-100;
  margin: 0;

  @include caption($color-deep-blue-100);
}

.boxDisabled {
  color: $color-grey-70;
  background-color: $color-deep-blue-10;
  border-color: $color-grey-70;

  .pricing {
    border-color: $color-grey-70;
  }
}

.promotionTypeCard .pricing {
  border-top: 1px solid $toplisting-color;
}

.promotionTypeCard {
  display: flex;
  flex-direction: column;
  height: calc(100% - $space-150);
  margin-bottom: $form-field-margin-bottom;

  &.fillColumnHeight {
    flex: 1 1 auto;
    height: 100%;
    min-height: 0;
  }

  cursor: pointer;
  user-select: none;
  border: 1px solid $toplisting-color;
  border-top-width: 8px;
  border-radius: $border-radius-l;

  &:not(.boxDisabled):hover,
  &.boxSelected {
    margin-bottom: calc($form-field-margin-bottom - 1px);
    border-width: 2px;
    border-top-width: 8px;

    .benefits,
    .pricing {
      padding-left: calc($space-75 + 3px);
    }

    .header,
    .intro {
      padding-left: calc($space-100 - 1px);
    }

    .benefits {
      padding-right: calc($space-75 + 1px);
      padding-left: calc($space-250 - 1px);
    }
  }

  &:not(.boxDisabled):hover,
  &:not(.boxDisabled):focus-within {
    border-color: $toplisting-hover-color;
    box-shadow: $elevation-minimal;

    .pricing {
      border-color: $toplisting-hover-color;
    }
  }

  &.boxSelected {
    background-color: $toplisting-selected-background;
    border-color: $toplisting-selected-color;
    border-top-color: $toplisting-color;
    box-shadow: $elevation-medium;

    .pricing {
      border-color: $toplisting-selected-color;
    }
  }

  &.boxSelected:hover {
    border-color: $toplisting-hover-color;
    box-shadow: $elevation-medium;

    .pricing {
      border-color: $toplisting-hover-color;
    }
  }
}

.promotionTypeCard.hasAccent:not(.boxDisabled) {
  border-color: var(--ptc-border-accent);

  .pricing {
    border-top-color: var(--ptc-border-accent);
  }

  &:hover,
  &:focus-within {
    border-color: var(--ptc-border-accent-hover);
    box-shadow: $elevation-minimal;

    .pricing {
      border-color: var(--ptc-border-accent-hover);
    }
  }

  &.boxSelected {
    border-color: var(--ptc-border-accent);
    box-shadow: $elevation-medium;

    .pricing {
      border-color: var(--ptc-border-accent);
    }
  }

  &.boxSelected:hover {
    border-color: var(--ptc-border-accent-hover);
    box-shadow: $elevation-medium;

    .pricing {
      border-color: var(--ptc-border-accent-hover);
    }
  }
}

.header {
  @include heading-4;

  padding-bottom: 0;

  span {
    @include body-3-bold($color-deep-blue-100);
  }
}

.benefits {
  flex-grow: 1;

  @include body-3;

  & {
    padding-left: $space-250;
  }

  .benefitItem {
    position: relative;

    + .benefitItem {
      margin-top: $space-50;
    }
  }

  .checkIcon {
    position: absolute;
    top: 0;
    margin-left: -$space-200;

    path {
      fill: $color-seafoam-70;
    }
  }

  .benefitLineWrap {
    display: flex;
    flex-wrap: wrap;
    gap: $space-25;
    align-items: center;
    width: 100%;
    min-width: 0;
  }

  .benefitLine2TooltipWrap {
    position: relative;
    z-index: 1;
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
  }

  .benefitLine2Footnote {
    margin: 0;
  }
}

.benefitsImproved {
  @include caption;
}

@media screen and (max-width: $viewport-sm-min) {
  .promotionTypeCard {
    height: auto;
    margin-right: 0;
    margin-left: 0;

    &.fillColumnHeight {
      flex: none;
      height: auto;
    }
  }
}
</style>
